import type { productFormState } from '~/types/local-types'
import cloneDeep from 'lodash.clonedeep'
import { showExtrasModal } from '~/composables/showExtrasModal'
import { shop } from '~/store/shop'

export const productForm = defineStore('productForm', {
    state: (): productFormState => ({
        currentProductWithSelectedExtras: {},
        extraDetailsInfo: {}
    } as productFormState),
    getters: {
        totalSelectedItemsForExtra (state) {
            return (extraId: string) => {
                return Object.values(state.currentProductWithSelectedExtras.extras[extraId].items).reduce((total: number, extraItem: any) => {
                    return Number(total) + Number(extraItem.quantity)
                }, 0)
            }
        },
        extraItemMaxQuantity (state) {
            return (extraId: string, itemId: string, maxForExtra: number, maxForExtraItem: number) => {
                const maxForEntireExtra = maxForExtra > 0 ? state.currentProductWithSelectedExtras.extras[extraId].items[itemId].quantity + (maxForExtra - this.totalSelectedItemsForExtra(extraId)) : 9999
                return maxForExtraItem !== 0 && maxForExtraItem < maxForEntireExtra ? maxForExtraItem : maxForEntireExtra
            }
        },
        extraItemIsDisabled (state) {
            return (extraId: string, itemId: string, maxForExtra: number) => {
                if (maxForExtra === 1) {
                    return false
                } else {
                    return state.currentProductWithSelectedExtras.extras[extraId].items[itemId].disabled === true || (state.currentProductWithSelectedExtras.extras[extraId].items[itemId].quantity === 0 && maxForExtra > 0 && this.totalSelectedItemsForExtra(extraId) >= maxForExtra)
                }
            }
        },
        isExtraConditionMet (state) {
            return (extra: any) => {
                return !!extra.showOnItemId?.find((item: any) => {
                    return state.currentProductWithSelectedExtras.extras[extra.showOnExtraId]
                      && state.currentProductWithSelectedExtras.extras[extra.showOnExtraId].items[item].selected === true
                })
            }
        }
    },
    actions: {
        setExtraDetailsInfo (extraDetailsInfo: any) {
            this.extraDetailsInfo = extraDetailsInfo
        },
        async editProductFromCart (cartProduct: any, productKeyToReplace: any) {
            const currentProduct = shop().findProduct(cartProduct.id)
            if (currentProduct) {
                this.prepareModalProductFormModel(currentProduct, cartProduct)
                await showExtrasModal(currentProduct, productKeyToReplace)
            }
        },
        async openModalFromProduct (product: any) {
            this.prepareModalProductFormModel(product)
            await showExtrasModal(product)
        },
        prepareModalProductFormModel (product: any, cartProduct?: any) {
            product = cloneDeep(product)
            cartProduct = cloneDeep(cartProduct)
            const formModel: any = {
                id: product.id,
                quantity: cartProduct?.quantity || 1,
                note: cartProduct?.note || ''
            }
            if (product.extras) {
                formModel.extras = {}
                Object.keys(product.extras).forEach(extraKey => {
                    formModel.extras[product.extras[extraKey].id] = {
                        items: {}
                    }
                    if (product?.extras[extraKey]?.items && Object.keys(product.extras[extraKey].items).length > 0) {
                        Object.keys(product.extras[extraKey].items).forEach(itemKey => {
                            const extraId = product.extras[extraKey].id
                            const itemId = product.extras[extraKey].items[itemKey].id
                            if (cartProduct) {
                                // Populate premade extra choices from cartProduct if they exist
                                if (cartProduct.extras && Object.keys(cartProduct.extras).length && cartProduct?.extras[extraId]?.items[itemId]) {
                                    formModel.extras[extraId].items[itemId] = cartProduct.extras[extraId].items[itemId]
                                } else {
                                    formModel.extras[extraId].items[itemId] = {
                                        selected: false,
                                        disabled: false,
                                        quantity: 0
                                    }
                                }
                            } else {
                                // Else set the default values that the backend has given us
                                const currentItem = product.extras[extraKey].items[itemKey]
                                formModel.extras[extraId].items[itemId] = {
                                    selected: currentItem.isSelected,
                                    disabled: currentItem.isDisabled,
                                    quantity: currentItem.isSelected ? 1 : 0
                                }
                            }
                        })
                    }
                })
            }
            this.currentProductWithSelectedExtras = formModel
        },
        extraItemCheckedLogic (extraId: number, itemId: number, maxForExtra?: number) {
            const value = !this.currentProductWithSelectedExtras.extras[extraId].items[itemId].selected
            if (maxForExtra === 1) {
                Object.keys(this.currentProductWithSelectedExtras.extras[extraId].items).forEach(itemKey => {
                    this.currentProductWithSelectedExtras.extras[extraId].items[itemKey].selected = false
                    this.currentProductWithSelectedExtras.extras[extraId].items[itemKey].quantity = 0
                })
                this.currentProductWithSelectedExtras.extras[extraId].items[itemId].selected = value
                this.currentProductWithSelectedExtras.extras[extraId].items[itemId].quantity = value ? 1 : 0
            } else {
                if (!value && Number(this.currentProductWithSelectedExtras.extras[extraId].items[itemId].quantity) > 0) {
                    this.currentProductWithSelectedExtras.extras[extraId].items[itemId].quantity = 0
                } else if (value && Number(this.currentProductWithSelectedExtras.extras[extraId].items[itemId].quantity) === 0) {
                    this.currentProductWithSelectedExtras.extras[extraId].items[itemId].quantity = 1
                }
                this.currentProductWithSelectedExtras.extras[extraId].items[itemId].selected = value
                this.extraItemConditionalLogic()
            }
        },
        extraItemQuantityLogic (value: number, extraId: number, itemId: number) {
            if (Number(value) === 0 && this.currentProductWithSelectedExtras.extras[extraId].items[itemId].selected === true) {
                this.currentProductWithSelectedExtras.extras[extraId].items[itemId].selected = false
            } else if (Number(value) > 0 && this.currentProductWithSelectedExtras.extras[extraId].items[itemId].selected === false) {
                this.currentProductWithSelectedExtras.extras[extraId].items[itemId].selected = true
            }
            this.currentProductWithSelectedExtras.extras[extraId].items[itemId].quantity = value
            this.extraItemConditionalLogic()
        },
        extraItemConditionalLogic () {
            Object.keys(this.currentProductWithSelectedExtras.extras).forEach(extraKey => {
                const currentExtra = shop().findExtra(extraKey)
                if (currentExtra?.isConditional === true && !this.isExtraConditionMet(currentExtra)) {
                    Object.keys(this.currentProductWithSelectedExtras.extras[extraKey].items).forEach(itemKey => {
                        this.currentProductWithSelectedExtras.extras[extraKey].items[itemKey].selected = false
                        this.currentProductWithSelectedExtras.extras[extraKey].items[itemKey].quantity = 0
                    })
                }
            })
        },
        resetCurrentProductWithSelectedExtras () {
            this.currentProductWithSelectedExtras = []
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(productForm, import.meta.hot))
}
